/**
 * GroupPostingFlow — "Zdobądź pierwsze zapytania"
 * 1 klik → gotowy post → kopiuj → otwórz → done
 * Zero dodatkowych kroków. User NIE myśli.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Copy, Download, ExternalLink, CheckCircle2,
  Loader2, Plus, Trash2, MapPin, Briefcase, Globe, Sparkles,
  Zap, TrendingUp, Camera, Image, AlertTriangle, ChevronRight,
  Smartphone, Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PanelLayout from '@/components/PanelLayout';
import ProfileGate from '@/components/ProfileGate';
import { API } from '@/utils/api';
import { GTM } from '@/utils/gtm';

import GroupScanner from '@/components/GroupScanner';
import { useTranslation } from 'react-i18next';

const REASON_META = {
  lokalizacja: { icon: MapPin, badge: 'Lokalna', color: '#10B981' },
  'branżowa': { icon: Briefcase, badge: 'Branżowa', color: '#3B82F6' },
  'szeroki zasięg': { icon: Globe, badge: 'Duży zasięg', color: '#F47A3B' },
  ogólna: { icon: Globe, badge: 'Popularna', color: '#F47A3B' },
  biznesowa: { icon: Briefcase, badge: 'Biznesowa', color: '#F47A3B' },
  rasa: { icon: Sparkles, badge: 'Rasowa', color: '#EC4899' },
};

const fmtMembers = (n) => {
  if (!n) return null;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k członków`;
  return `${n} członków`;
};

export default function GroupPostingFlow({ embedded = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [groups, setGroups] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [postedGroups, setPostedGroups] = useState(new Set());
  const [copied, setCopied] = useState(false);
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupUrl, setNewGroupUrl] = useState('');
  const [hasPhotos, setHasPhotos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoOptions, setPromoOptions] = useState([]);
  const [showPromoChange, setShowPromoChange] = useState(false);
  const [tab, setTab] = useState('publish');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groups/list`, { headers });
      setGroups(res.data.groups || []);
      setSuggestions(res.data.suggestions || []);
      setHasPhotos(res.data.has_photos ?? false);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── 1 KLIK = GOTOWY POST ── */
  const handleMainCTA = async (promoteType, promoteId) => {
    if (!hasPhotos) {
      toast.error('Dodaj zdjęcia do galerii, aby zdobyć zapytania');
      return;
    }
    setGenerating(true);
    try {
      const body = {};
      if (promoteType) { body.promote_type = promoteType; body.promote_id = promoteId || null; }
      const res = await axios.post(`${API}/groups/generate-post`, body, { headers });
      setPost(res.data);
      setPromoOptions(res.data.available_options || []);
      setPostedGroups(new Set());
      setShowPromoChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Coś poszło nie tak');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyText = async () => {
    if (!post?.text) return;
    try {
      await navigator.clipboard.writeText(post.text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = post.text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success('Skopiowane — wklej w grupie');
    setTimeout(() => setCopied(false), 5000);
  };

  const handleDownloadImage = () => {
    if (!post?.image_url) return;
    const a = document.createElement('a');
    a.href = post.image_url;
    a.download = 'post-grupa.jpg';
    a.target = '_blank';
    a.click();
    toast.success('Zapisano — dodaj do posta na Facebooku');
  };

  const handleWhatsApp = () => {
    if (!post?.text) return;
    const text = encodeURIComponent(post.text);
    window.open(`https://wa.me/?text=${text}`, '_blank');
    toast.success('Otwarto WhatsApp — wyślij do znajomych lub na grupę');
  };

  const handleMarkPosted = async (groupId) => {
    try {
      await axios.post(`${API}/groups/mark-posted`, { group_id: groupId, post_id: post?.post_id }, { headers });
      setPostedGroups(prev => new Set([...prev, groupId]));
      GTM.pagePublished('facebook_group');
    } catch {}
  };

  const handleSaveGroup = async () => {
    if (!newGroupName || !newGroupUrl) return;
    try {
      await axios.post(`${API}/groups/save`, { name: newGroupName, url: newGroupUrl }, { headers });
      setNewGroupName('');
      setNewGroupUrl('');
      setAddingGroup(false);
      fetchData();
      toast.success('Dodano — pojawi się poniżej');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nie udało się');
    }
  };

  const handleAddSuggestion = async (sug) => {
    try {
      await axios.post(`${API}/groups/save`, { name: sug.name, url: sug.url }, { headers });
      fetchData();
      toast.success(`Dodano: ${sug.name}`);
    } catch {}
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await axios.delete(`${API}/groups/${groupId}`, { headers });
      setGroups(prev => prev.filter(g => g.id !== groupId));
      toast.success('Usunięto');
    } catch {}
  };

  const totalGroups = groups.length;
  const postedCount = postedGroups.size;
  const remaining = totalGroups - postedCount;
  const allPosted = postedCount > 0 && postedCount >= totalGroups;

  if (loading) {
    const loadingContent = (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-6 h-6 animate-spin text-[#F47A3B]" />
      </div>
    );
    return embedded ? loadingContent : <PanelLayout>{loadingContent}</PanelLayout>;
  }

  const content = (
    <ProfileGate>
      <div className="max-w-[720px] mx-auto pb-16" data-testid="group-posting-page">

        {/* ═══════ TAB NAVIGATION ═══════ */}
        <div className="flex gap-1 mb-6 bg-zinc-100 p-1 rounded-xl" data-testid="group-tabs">
          <button
            onClick={() => setTab('publish')}
            data-testid="tab-publish"
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              tab === 'publish'
                ? 'bg-white text-zinc-900 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            Publikowanie
          </button>
          <button
            onClick={() => setTab('scanner')}
            data-testid="tab-scanner"
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
              tab === 'scanner'
                ? 'bg-white text-zinc-900 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <Target className="w-3.5 h-3.5" />
            Polecane grupy
          </button>
        </div>

        {/* ═══════ SCANNER TAB ═══════ */}
        {tab === 'scanner' && <GroupScanner />}

        {/* ═══════ PUBLISH TAB ═══════ */}
        {tab === 'publish' && (<>

        {/* ═══════ HEADER — only standalone ═══════ */}
        {!embedded && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center pt-4 pb-6"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/15 mb-4">
            <Zap className="w-3.5 h-3.5 text-[#F47A3B]" />
            <span className="text-[12px] font-semibold text-[#F47A3B]">Najszybszy sposób na pierwsze zapytania</span>
          </div>
          <h1 className="text-3xl font-extrabold text-zinc-900 mb-2" data-testid="hero-title">
            Zdobądź pierwsze zapytania
          </h1>
          <p className="text-[15px] text-zinc-500 max-w-md mx-auto mb-6">
            {post
              ? 'System przygotował post — opublikuj go w grupach w 2 minuty'
              : 'Gotowy post + grupy + 2 kliknięcia = zapytania od klientów'}
          </p>

          {/* MAIN CTA — only before post is generated */}
          {!post && hasPhotos && (
            <Button
              onClick={handleMainCTA}
              disabled={generating}
              className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-full px-10 py-4 text-[16px] font-bold shadow-lg shadow-[#F47A3B]/25 hover:shadow-xl hover:shadow-[#F47A3B]/30 transition-all"
              data-testid="main-cta-btn"
            >
              {generating ? (
                <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Przygotowuję...</>
              ) : (
                <><Sparkles className="w-5 h-5 mr-2" /> Zdobądź pierwsze zapytania</>
              )}
            </Button>
          )}
        </motion.div>
        )}

        <div className="space-y-5">

          {/* ═══════ ALERT: BRAK ZDJĘĆ ═══════ */}
          {!hasPhotos && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="bg-white rounded-2xl border border-zinc-200 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="photo-alert">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
                    <Camera className="w-5 h-5 text-[#F47A3B]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[15px] font-bold text-zinc-900 mb-0.5">
                      Zdjęcia = więcej zapytań
                    </h3>
                    <p className="text-[13px] text-zinc-500 mb-3">
                      Posty ze zdjęciem dostają nawet 3× więcej reakcji. Dodaj 2–3 zdjęcia swojej oferty.
                    </p>
                    <Button
                      onClick={() => navigate('/panel/galeria')}
                      className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl px-6 py-2.5 text-[13px] font-bold shadow-md shadow-[#F47A3B]/20 transition-all"
                      data-testid="add-photos-btn"
                    >
                      <Image className="w-4 h-4 mr-2" />
                      Dodaj zdjęcia
                    </Button>
                    <p className="text-[11px] text-zinc-400 mt-2 flex items-center gap-1.5">
                      <AlertTriangle className="w-3 h-3" />
                      Bez zdjęć post będzie mniej skuteczny.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* ═══════ PROMUJEMY TERAZ ═══════ */}
          {post && post.promoting && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="bg-[#F47A3B]/10 rounded-2xl border border-[#F47A3B]/15 p-4" data-testid="promoting-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#F47A3B]/10 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-[#F47A3B]" />
                    </div>
                    <div>
                      <p className="text-[11px] text-zinc-500">Promujemy teraz</p>
                      <p className="text-[14px] font-bold text-zinc-900" data-testid="promoting-label">{post.promoting}</p>
                      {post.promoting_sublabel && <p className="text-[11px] text-zinc-400">{post.promoting_sublabel}</p>}
                    </div>
                  </div>
                  {promoOptions.length > 1 && (
                    <div className="relative">
                      <button
                        onClick={() => setShowPromoChange(!showPromoChange)}
                        className="text-[12px] text-[#F47A3B] hover:underline font-medium"
                        data-testid="change-promo-btn"
                      >
                        Zmień
                      </button>
                      <AnimatePresence>
                        {showPromoChange && (
                          <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            className="absolute right-0 top-8 bg-white rounded-xl border border-zinc-200 shadow-lg z-20 min-w-[240px] overflow-hidden"
                            data-testid="promo-dropdown"
                          >
                            {promoOptions.map((opt, i) => (
                              <button
                                key={i}
                                onClick={() => handleMainCTA(opt.type, opt.id)}
                                disabled={generating}
                                className={`w-full text-left px-4 py-3 hover:bg-[#F47A3B]/10 transition-colors border-b border-[#F3F4F6] last:border-0 ${
                                  post.promote_type === opt.type && (!opt.id || post.promoted_id === opt.id) ? 'bg-[#F47A3B]/10' : ''
                                }`}
                                data-testid={`promo-option-${opt.type}`}
                              >
                                <p className="text-[13px] font-semibold text-zinc-900">{opt.label}</p>
                                <p className="text-[11px] text-zinc-400">{opt.sublabel}</p>
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-zinc-400 mt-2">Dopasowane do Twojego profilu</p>
              </div>
            </motion.div>
          )}

          {/* ═══════ GOTOWY POST (po kliknięciu CTA) ═══════ */}
          {post && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="step-copy-post">
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    <span className="text-[14px] font-bold text-zinc-900">Gotowy post</span>
                  </div>

                  {/* Post preview — Facebook-style */}
                  <div className="rounded-xl border border-[#CED0D4] bg-white overflow-hidden" data-testid="fb-preview-frame">
                    {/* FB Header */}
                    <div className="flex items-center gap-3 px-4 pt-4 pb-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F47A3B] to-[#F9A66C] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        A
                      </div>
                      <div className="flex-1">
                        <p className="text-[14px] font-semibold text-[#050505] leading-tight">Twoja oferta</p>
                        <div className="flex items-center gap-1 text-[12px] text-[#65676B]">
                          <span>Właśnie teraz</span>
                          <span>·</span>
                          <Globe className="w-3 h-3" />
                        </div>
                      </div>
                    </div>
                    {/* FB Body */}
                    <div className="px-4 pb-3">
                      <p className="text-[15px] text-[#050505] leading-relaxed whitespace-pre-line" data-testid="group-post-text">{post.text}</p>
                    </div>
                    {post.image_url && (
                      <img src={post.image_url} alt="" className="w-full max-h-[280px] object-cover border-t border-[#CED0D4]" />
                    )}
                    {/* FB Reactions bar — empty preview */}
                    <div className="px-4 py-2.5 border-t border-[#CED0D4]">
                      <div className="flex items-center justify-between text-[13px] text-[#65676B]">
                        <span className="text-[12px]">Podgląd Twojego posta</span>
                      </div>
                    </div>
                    {/* FB Actions */}
                    <div className="flex border-t border-[#CED0D4]">
                      <button className="flex-1 flex items-center justify-center gap-2 py-2.5 text-[13px] font-semibold text-[#65676B] hover:bg-[#F2F3F5] transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" /></svg>
                        Lubię to
                      </button>
                      <button className="flex-1 flex items-center justify-center gap-2 py-2.5 text-[13px] font-semibold text-[#65676B] hover:bg-[#F2F3F5] transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                        Komentarz
                      </button>
                      <button className="flex-1 flex items-center justify-center gap-2 py-2.5 text-[13px] font-semibold text-[#65676B] hover:bg-[#F2F3F5] transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                        Udostępnij
                      </button>
                    </div>
                  </div>
                  <p className="text-[10px] text-zinc-400 text-center mt-2">Podgląd — tak będzie wyglądać Twój post w grupie</p>

                  {/* Action buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={handleCopyText}
                      className={`flex-1 rounded-xl text-[14px] font-bold py-3 transition-all ${
                        copied ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-[#F47A3B] hover:bg-[#E06A2E]'
                      } text-white`}
                      data-testid="copy-text-btn"
                    >
                      {copied ? <><CheckCircle2 className="w-4 h-4 mr-2" /> Skopiowane!</> : <><Copy className="w-4 h-4 mr-2" /> Skopiuj tekst</>}
                    </Button>
                    {post.image_url && (
                      <Button
                        onClick={handleDownloadImage}
                        variant="outline"
                        className="rounded-xl text-[13px] border-zinc-200 text-zinc-500 hover:border-[#F47A3B]/30"
                        data-testid="download-image-btn"
                      >
                        <Download className="w-4 h-4 mr-1.5" /> Zapisz zdjęcie
                      </Button>
                    )}
                  </div>

                  {/* WhatsApp deep link */}
                  <button
                    onClick={handleWhatsApp}
                    className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 text-[#128C7E] hover:bg-[#25D366]/10 transition-all text-[13px] font-semibold"
                    data-testid="whatsapp-btn"
                  >
                    <Smartphone className="w-4 h-4" />
                    Wyślij na telefon
                  </button>
                  <p className="text-[10px] text-zinc-400 text-center mt-1">Otworzy WhatsApp z gotowym postem</p>

                  <button
                    onClick={handleMainCTA}
                    disabled={generating}
                    className="mt-3 w-full text-[11px] text-zinc-400 hover:text-[#F47A3B] transition-colors"
                    data-testid="regenerate-post-btn"
                  >
                    {generating ? 'Generuję...' : 'Wygeneruj inny post'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* ═══════ CONNECTOR ═══════ */}
          {post && <div className="flex justify-center"><div className="w-0.5 h-6 bg-[#E8EAED]" /></div>}

          {/* ═══════ GRUPY ═══════ */}
          {post && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="bg-white rounded-2xl border border-zinc-200 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="step-groups">

                {/* User's groups with Open + Mark buttons */}
                {groups.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-[14px] font-bold text-zinc-900 mb-1">Twoje grupy</h3>
                    <p className="text-[11px] text-zinc-400 mb-3">W większości grup wystarczy kliknąć „Dołącz", aby opublikować post</p>
                    <div className="space-y-2">
                      {groups.map((g, i) => {
                        const isMarked = postedGroups.has(g.id);
                        return (
                          <motion.div
                            key={g.id}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.03 }}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                              isMarked ? 'bg-[#F0FDF4] border-emerald-200' : 'bg-white border-zinc-200 hover:border-[#F47A3B]/30 hover:shadow-sm'
                            }`}
                            data-testid={`group-${g.id}`}
                          >
                            {isMarked ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                            ) : (
                              <Users className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-[13px] font-semibold text-zinc-900 truncate">{g.name}</p>
                            </div>
                            {!isMarked ? (
                              <div className="flex items-center gap-2">
                                <div className="text-right">
                                  <Button
                                    size="sm"
                                    onClick={() => window.open(g.url, '_blank')}
                                    className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-lg text-[12px] px-4 h-8 font-semibold"
                                    data-testid={`open-group-${g.id}`}
                                  >
                                    Otwórz <ExternalLink className="w-3 h-3 ml-1.5" />
                                  </Button>
                                  <p className="text-[10px] text-zinc-400 mt-0.5">Dołącz i wklej post</p>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleMarkPosted(g.id)}
                                  className="rounded-lg text-[12px] h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-medium"
                                  data-testid={`mark-posted-${g.id}`}
                                >
                                  Oznacz
                                </Button>
                                <button onClick={() => handleDeleteGroup(g.id)} className="text-[#D1D5DB] hover:text-red-400 p-1">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : (
                              <span className="text-[12px] font-semibold text-emerald-600 flex items-center gap-1">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Oznaczone
                              </span>
                            )}
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Suggestions */}
                {suggestions.length > 0 && (
                  <div className="mb-4">
                    {groups.length === 0 && <h3 className="text-[14px] font-bold text-zinc-900 mb-1">Dodaj grupy, aby zacząć</h3>}
                    {groups.length > 0 && <h3 className="text-[13px] font-semibold text-zinc-500 mb-2">Polecane grupy</h3>}
                    <div className="space-y-2">
                      {suggestions.map((s, i) => {
                        const meta = REASON_META[s.reason] || REASON_META.ogólna;
                        const Icon = meta.icon;
                        return (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 + i * 0.04 }}
                            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#FAFBFC] border border-dashed border-zinc-200 hover:border-[#F47A3B]/30 hover:bg-white transition-all"
                          >
                            <div className="w-8 h-8 rounded-lg bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
                              <Icon className="w-4 h-4" style={{ color: meta.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[13px] font-semibold text-zinc-900">{s.name}</p>
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md" style={{ color: meta.color, background: `${meta.color}12` }}>
                                {meta.badge}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleAddSuggestion(s)}
                              className="bg-[#F47A3B]/10 hover:bg-[#F47A3B] text-[#F47A3B] hover:text-white rounded-lg text-[12px] h-8 font-semibold border border-[#F47A3B]/20 transition-all"
                            >
                              <Plus className="w-3 h-3 mr-1" /> Dodaj
                            </Button>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Empty groups state */}
                {groups.length === 0 && suggestions.length === 0 && (
                  <div className="text-center py-6" data-testid="empty-groups">
                    <Users className="w-8 h-8 text-[#F47A3B] mx-auto mb-2" />
                    <p className="text-[13px] text-zinc-500">Dodaj pierwsze grupy — to najszybszy sposób na zapytania</p>
                  </div>
                )}

                {/* Add custom group */}
                <AnimatePresence>
                  {addingGroup ? (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="p-4 bg-[#F8FAFC] rounded-xl border border-zinc-200">
                      <div className="flex gap-2 mb-2">
                        <Input placeholder="Nazwa grupy" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} className="text-[13px] rounded-lg" data-testid="new-group-name" />
                        <Input placeholder="Link do grupy" value={newGroupUrl} onChange={e => setNewGroupUrl(e.target.value)} className="text-[13px] rounded-lg flex-[2]" data-testid="new-group-url" />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleSaveGroup} size="sm" className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-lg text-[12px]" data-testid="save-group-btn">Zapisz</Button>
                        <Button onClick={() => setAddingGroup(false)} size="sm" variant="ghost" className="text-[12px]">Anuluj</Button>
                      </div>
                    </motion.div>
                  ) : (
                    <button
                      onClick={() => setAddingGroup(true)}
                      className="flex items-center gap-2 text-[12px] text-zinc-400 hover:text-[#F47A3B] transition-colors mt-2"
                      data-testid="add-group-toggle"
                    >
                      <Plus className="w-3.5 h-3.5" /> Dodaj własną grupę
                    </button>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {/* ═══════ GRUPY (przed generowaniem — gdy brak posta ale są zdjęcia) ═══════ */}
          {!post && hasPhotos && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-white rounded-2xl border border-zinc-200 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
                {suggestions.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-[14px] font-bold text-zinc-900 mb-1">Polecane grupy dla Ciebie</h3>
                    <p className="text-[12px] text-zinc-500 mb-3">Dodaj 3–5 grup, aby zdobyć pierwsze zapytania</p>
                    <div className="space-y-2">
                      {suggestions.map((s, i) => {
                        const meta = REASON_META[s.reason] || REASON_META.ogólna;
                        const Icon = meta.icon;
                        return (
                          <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#FAFBFC] border border-dashed border-zinc-200">
                            <div className="w-8 h-8 rounded-lg bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
                              <Icon className="w-4 h-4" style={{ color: meta.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[13px] font-semibold text-zinc-900">{s.name}</p>
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md" style={{ color: meta.color, background: `${meta.color}12` }}>{meta.badge}</span>
                            {s.members && <span className="text-[10px] text-zinc-400 ml-1">{fmtMembers(s.members)}</span>}
                            </div>
                            <Button size="sm" onClick={() => handleAddSuggestion(s)} className="bg-[#F47A3B]/10 hover:bg-[#F47A3B] text-[#F47A3B] hover:text-white rounded-lg text-[12px] h-8 font-semibold border border-[#F47A3B]/20 transition-all">
                              <Plus className="w-3 h-3 mr-1" /> Dodaj
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {groups.length > 0 && (
                  <div>
                    <h3 className="text-[14px] font-bold text-zinc-900 mb-3">Twoje grupy ({groups.length})</h3>
                    <div className="space-y-2">
                      {groups.map(g => (
                        <div key={g.id} className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-zinc-200">
                          <Users className="w-4 h-4 text-zinc-400" />
                          <span className="text-[13px] text-zinc-900 flex-1 truncate">{g.name}</span>
                          <button onClick={() => handleDeleteGroup(g.id)} className="text-[#D1D5DB] hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* ═══════ GRUPY (brak zdjęć — zawsze widoczne) ═══════ */}
          {!hasPhotos && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex justify-center mb-1"><div className="w-0.5 h-6 bg-[#E8EAED]" /></div>
              <div className="bg-white rounded-2xl border border-zinc-200 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
                <h3 className="text-[14px] font-bold text-zinc-900 mb-1">Polecane grupy dla Ciebie</h3>
                <p className="text-[12px] text-zinc-500 mb-3">Dodaj grupy — będą gotowe, gdy dodasz zdjęcia</p>
                {suggestions.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {suggestions.map((s, i) => {
                      const meta = REASON_META[s.reason] || REASON_META.ogólna;
                      const Icon = meta.icon;
                      return (
                        <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#FAFBFC] border border-dashed border-zinc-200">
                          <div className="w-8 h-8 rounded-lg bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4" style={{ color: meta.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold text-zinc-900">{s.name}</p>
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md" style={{ color: meta.color, background: `${meta.color}12` }}>{meta.badge}</span>
                            {s.members && <span className="text-[10px] text-zinc-400 ml-1">{fmtMembers(s.members)}</span>}
                          </div>
                          <Button size="sm" onClick={() => handleAddSuggestion(s)} className="bg-[#F47A3B]/10 hover:bg-[#F47A3B] text-[#F47A3B] hover:text-white rounded-lg text-[12px] h-8 font-semibold border border-[#F47A3B]/20 transition-all">
                            <Plus className="w-3 h-3 mr-1" /> Dodaj
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {groups.length > 0 && (
                  <div className="space-y-2">
                    {groups.map(g => (
                      <div key={g.id} className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-zinc-200">
                        <Users className="w-4 h-4 text-zinc-400" />
                        <span className="text-[13px] text-zinc-900 flex-1 truncate">{g.name}</span>
                        <button onClick={() => handleDeleteGroup(g.id)} className="text-[#D1D5DB] hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <AnimatePresence>
                  {addingGroup ? (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="p-4 mt-2 bg-[#F8FAFC] rounded-xl border border-zinc-200">
                      <div className="flex gap-2 mb-2">
                        <Input placeholder="Nazwa grupy" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} className="text-[13px] rounded-lg" />
                        <Input placeholder="Link do grupy" value={newGroupUrl} onChange={e => setNewGroupUrl(e.target.value)} className="text-[13px] rounded-lg flex-[2]" />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleSaveGroup} size="sm" className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-lg text-[12px]">Zapisz</Button>
                        <Button onClick={() => setAddingGroup(false)} size="sm" variant="ghost" className="text-[12px]">Anuluj</Button>
                      </div>
                    </motion.div>
                  ) : (
                    <button onClick={() => setAddingGroup(true)} className="flex items-center gap-2 text-[12px] text-zinc-400 hover:text-[#F47A3B] transition-colors mt-2" data-testid="add-group-toggle-nophoto">
                      <Plus className="w-3.5 h-3.5" /> Dodaj własną grupę
                    </button>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {/* ═══════ SOFT GATE: MIN 3 GRUPY ═══════ */}
          {post && totalGroups > 0 && totalGroups < 3 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
            >
              <div className="bg-[#FFFBEB] rounded-2xl border border-amber-200 p-4 flex items-start gap-3" data-testid="soft-gate">
                <Zap className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[13px] font-bold text-zinc-900">Dodaj min. 3 grupy, aby zwiększyć szansę na pierwsze zapytania</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">Masz {totalGroups} {totalGroups === 1 ? 'grupę' : 'grupy'} — dodaj jeszcze {3 - totalGroups}</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* ═══════ MOTIVATION ═══════ */}
          {post && totalGroups >= 3 && postedCount === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 }}
            >
              <div className="bg-gradient-to-br from-[#FFF3EC] to-[#FFF3EC] rounded-2xl border border-[#F47A3B]/15 p-4 flex items-start gap-3" data-testid="motivation-hint">
                <TrendingUp className="w-5 h-5 text-[#F47A3B] flex-shrink-0 mt-0.5" />
                <p className="text-[13px] font-semibold text-zinc-900">
                  Najwięcej pierwszych zapytań pojawia się po publikacji w 3–5 grupach
                </p>
              </div>
            </motion.div>
          )}

          {/* ═══════ PROGRESS ═══════ */}
          {post && totalGroups > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15 }}
            >
              <div className="bg-white rounded-2xl border-2 border-[#F47A3B]/20 p-5 shadow-[0_4px_20px_rgba(139,92,246,0.08)]" data-testid="progress-bar-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-[#F47A3B]" />
                    <span className="text-[14px] font-bold text-zinc-900">
                      {postedCount === 0 && `Gotowe do publikacji — ${totalGroups} grup czeka`}
                      {postedCount > 0 && !allPosted && `Opublikowano w ${postedCount} — zostało ${remaining}`}
                      {allPosted && 'Wszystko opublikowane!'}
                    </span>
                  </div>
                  <span className="text-[20px] font-extrabold text-[#F47A3B]">{postedCount} / {totalGroups}</span>
                </div>
                <div className="h-3 bg-[#F3F4F6] rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: allPosted ? 'linear-gradient(90deg, #10B981, #34D399)' : 'linear-gradient(90deg, #F47A3B, #A78BFA)' }}
                    initial={{ width: 0 }}
                    animate={{ width: `${totalGroups > 0 ? Math.round((postedCount / totalGroups) * 100) : 0}%` }}
                    transition={{ duration: 0.6 }}
                  />
                </div>
                {postedCount > 0 && !allPosted && (
                  <p className="text-[12px] text-zinc-500 mt-2">
                    Zostało {remaining} {remaining === 1 ? 'grupa' : remaining < 5 ? 'grupy' : 'grup'} — dokończ, aby zwiększyć szansę na zapytania
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {/* ═══════ SUCCESS STATE ═══════ */}
          {allPosted && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-[#F0FDF4] rounded-2xl border-2 border-emerald-200 p-6 text-center shadow-[0_4px_20px_rgba(16,185,129,0.08)]" data-testid="success-state">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-[16px] font-bold text-emerald-800 mb-1">Opublikowano w {postedCount} grupach</h3>
                <p className="text-[13px] text-emerald-600 mb-3">Teraz czekaj na pierwsze wiadomości — odpowiadaj szybko!</p>
                <Button
                  onClick={() => navigate('/panel')}
                  variant="outline"
                  className="rounded-xl text-[13px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  Wróć do panelu <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            </motion.div>
          )}

        </div>
        </>)}
      </div>
      </ProfileGate>
  );

  return embedded ? content : <PanelLayout>{content}</PanelLayout>;
}
